import React, { useState, useEffect, useRef } from 'react';
import { HiOutlineX } from 'react-icons/hi';
import axiosInstance from '../../api/axiosConfig';
import { NATIONALITIES } from '../../utils/nationalities';
import './CustomerForm.css';

const CustomerForm = ({ customer, onClose }) => {
    const [formData, setFormData] = useState({
        name: '',
        mobileNo: '',
        nationality: '',
        zone: '',
        zoneName: '',
        street: '',
        building: '',
        otherAddress: '',
        salesman: ''
    });
    const [loading, setLoading] = useState(false);

    const [zones, setZones] = useState([]);
    const [streets, setStreets] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [loadingZones, setLoadingZones] = useState(false);
    const [loadingStreets, setLoadingStreets] = useState(false);
    const [loadingBuildings, setLoadingBuildings] = useState(false);
    const [zoneSearch, setZoneSearch] = useState('');
    const [streetSearch, setStreetSearch] = useState('');
    const [buildingSearch, setBuildingSearch] = useState('');
    const [showZoneDropdown, setShowZoneDropdown] = useState(false);
    const [showStreetDropdown, setShowStreetDropdown] = useState(false);
    const [showBuildingDropdown, setShowBuildingDropdown] = useState(false);

    const [salesmen, setSalesmen] = useState([]);
    const [loadingSalesmen, setLoadingSalesmen] = useState(false);
    const [salesmanSearch, setSalesmanSearch] = useState('');
    const [showSalesmanDropdown, setShowSalesmanDropdown] = useState(false);

    const zoneDropdownRef = useRef(null);
    const streetDropdownRef = useRef(null);
    const buildingDropdownRef = useRef(null);
    const salesmanDropdownRef = useRef(null);
    const hasFetchedStreetsRef = useRef(false);
    const hasFetchedBuildingsRef = useRef(false);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (zoneDropdownRef.current && !zoneDropdownRef.current.contains(event.target)) {
                setShowZoneDropdown(false);
            }
            if (streetDropdownRef.current && !streetDropdownRef.current.contains(event.target)) {
                setShowStreetDropdown(false);
            }
            if (buildingDropdownRef.current && !buildingDropdownRef.current.contains(event.target)) {
                setShowBuildingDropdown(false);
            }
            if (salesmanDropdownRef.current && !salesmanDropdownRef.current.contains(event.target)) {
                setShowSalesmanDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        fetchZones();
    }, []);

    // Debounced salesman search — same 400ms pattern used for the address
    // fields' own typeahead below.
    useEffect(() => {
        const handle = setTimeout(() => {
            if (showSalesmanDropdown) fetchSalesmen(salesmanSearch);
        }, 400);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [salesmanSearch, showSalesmanDropdown]);

    useEffect(() => {
        if (customer) {
            setFormData({
                name: customer.name || '',
                mobileNo: customer.mobileNo || '',
                nationality: customer.nationality || '',
                zone: customer.zone || '',
                zoneName: customer.zoneName || '',
                street: customer.street || '',
                building: customer.building || '',
                otherAddress: customer.otherAddress || '',
                salesman: customer.salesman?._id || ''
            });

            if (customer.salesman) {
                setSalesmanSearch(`${customer.salesman.name} (${customer.salesman.employeeId})`);
            }

            // Show exactly the district that was picked, not a re-guessed
            // first match — a QNAS zone number can cover several districts
            // (see Delivery.zoneName schema comment), so only the recorded
            // zoneName can be trusted. Legacy records with no zoneName fall
            // back to the number alone until the "zones loaded" effect below
            // does its best-effort match.
            if (customer.zone) {
                setZoneSearch(customer.zoneName ? `${customer.zone} - ${customer.zoneName}` : customer.zone);
            }
            if (customer.street) setStreetSearch(customer.street);
            if (customer.building) setBuildingSearch(customer.building);

            if (customer.zone && !hasFetchedStreetsRef.current) {
                hasFetchedStreetsRef.current = true;
                fetchStreets(customer.zone);
            }

            if (customer.zone && customer.street && !hasFetchedBuildingsRef.current) {
                hasFetchedBuildingsRef.current = true;
                fetchBuildings(customer.zone, customer.street);
            }
        }

        return () => {
            if (!customer) {
                hasFetchedStreetsRef.current = false;
                hasFetchedBuildingsRef.current = false;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customer]);

    // Legacy fallback only: records saved before zoneName existed have no
    // recorded district, so this is the best guess available (whichever
    // district QNAS lists first for that zone number). Records with a
    // zoneName never reach this — their zoneSearch was already set to the
    // real value above, so it never equals the bare customer.zone string.
    useEffect(() => {
        if (customer && !customer.zoneName && customer.zone && zones.length > 0 && zoneSearch === customer.zone) {
            const selectedZone = zones.find(z => z.zone_number.toString() === customer.zone.toString());
            if (selectedZone) {
                setZoneSearch(`${selectedZone.zone_number} - ${selectedZone.zone_name_en}`);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zones]);

    // Update street search display with full name after streets are loaded
    useEffect(() => {
        if (customer && customer.street && streets.length > 0 && streetSearch === customer.street) {
            const selectedStreet = streets.find(s => s.street_number.toString() === customer.street.toString());
            if (selectedStreet) {
                setStreetSearch(`${selectedStreet.street_number} - ${selectedStreet.street_name_en}`);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [streets]);

    const fetchZones = async () => {
        try {
            setLoadingZones(true);
            const response = await axiosInstance.get('/api/customers/qnas/zones');
            if (response.data.success && response.data.data) {
                const zonesData = response.data.data.zones || response.data.data;
                setZones(Array.isArray(zonesData) ? zonesData : []);
            }
        } catch (error) {
            console.error('Error fetching zones:', error);
        } finally {
            setLoadingZones(false);
        }
    };

    const fetchStreets = async (zoneNumber) => {
        if (!zoneNumber) return;
        try {
            setLoadingStreets(true);
            const response = await axiosInstance.get(`/api/customers/qnas/streets/${zoneNumber}`);
            if (response.data.success && response.data.data) {
                const streetsData = response.data.data.streets || response.data.data;
                const cleaned = (Array.isArray(streetsData) ? streetsData : []).filter(
                    s => s && (s.street_number != null || s.street_name_en || s.street_name_ar)
                );
                setStreets(cleaned);
            }
        } catch (error) {
            console.error('Error fetching streets:', error);
            setStreets([]);
        } finally {
            setLoadingStreets(false);
        }
    };

    const fetchBuildings = async (zoneNumber, streetNumber) => {
        if (!zoneNumber || !streetNumber) return;
        try {
            setLoadingBuildings(true);
            const response = await axiosInstance.get(`/api/customers/qnas/buildings/${zoneNumber}/${streetNumber}`);
            if (response.data.success && response.data.data) {
                const buildingsData = Array.isArray(response.data.data) ? response.data.data : [];
                setBuildings(buildingsData);
            }
        } catch (error) {
            console.error('Error fetching buildings:', error);
            setBuildings([]);
        } finally {
            setLoadingBuildings(false);
        }
    };

    const fetchSalesmen = async (search) => {
        try {
            setLoadingSalesmen(true);
            const response = await axiosInstance.get('/api/customers/salesmen', { params: { search } });
            if (response.data.success) {
                setSalesmen(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching salesmen:', error);
            setSalesmen([]);
        } finally {
            setLoadingSalesmen(false);
        }
    };

    const handleSalesmanSelect = (salesman) => {
        setFormData(prev => ({ ...prev, salesman: salesman._id }));
        setSalesmanSearch(`${salesman.name} (${salesman.employeeId})`);
        setShowSalesmanDropdown(false);
    };

    const handleClearSalesman = () => {
        setFormData(prev => ({ ...prev, salesman: '' }));
        setSalesmanSearch('');
    };

    const filteredZones = zones.filter(zone => {
        if (!zoneSearch.trim()) return true;
        const searchLower = zoneSearch.toLowerCase().trim();
        const zoneNum = (zone.zone_number ?? '').toString();
        const nameEn = (zone.zone_name_en ?? '').toLowerCase();
        const nameAr = zone.zone_name_ar ?? '';

        return (
            zoneNum === searchLower ||
            zoneNum.startsWith(searchLower) ||
            nameEn.includes(searchLower) ||
            nameAr.includes(zoneSearch.trim())
        );
    });

    const filteredStreets = streets.filter(street => {
        if (!streetSearch.trim()) return true;
        const searchLower = streetSearch.toLowerCase().trim();
        const streetNum = (street.street_number ?? '').toString();
        const nameEn = (street.street_name_en ?? '').toLowerCase();
        const nameAr = street.street_name_ar ?? '';

        return (
            streetNum === searchLower ||
            streetNum.startsWith(searchLower) ||
            nameEn.includes(searchLower) ||
            nameAr.includes(streetSearch.trim())
        );
    });

    const filteredBuildings = buildings.filter(building => {
        if (!buildingSearch.trim()) return true;
        const searchLower = buildingSearch.toLowerCase().trim();
        const buildingNum = (building.building_number ?? '').toString().toLowerCase();

        return (
            buildingNum === searchLower ||
            buildingNum.startsWith(searchLower)
        );
    });

    const handleZoneSelect = (zone) => {
        setFormData(prev => ({ ...prev, zone: zone.zone_number, zoneName: zone.zone_name_en, street: '', building: '' }));
        setZoneSearch(`${zone.zone_number} - ${zone.zone_name_en}`);
        setShowZoneDropdown(false);
        setStreets([]);
        setBuildings([]);
        setStreetSearch('');
        setBuildingSearch('');
        fetchStreets(zone.zone_number);
    };

    const handleStreetSelect = (street) => {
        setFormData(prev => ({ ...prev, street: street.street_number, building: '' }));
        setStreetSearch(`${street.street_number} - ${street.street_name_en}`);
        setShowStreetDropdown(false);
        setBuildings([]);
        setBuildingSearch('');
        fetchBuildings(formData.zone, street.street_number);
    };

    const handleBuildingSelect = (building) => {
        setFormData(prev => ({ ...prev, building: building.building_number }));
        setBuildingSearch(building.building_number);
        setShowBuildingDropdown(false);
    };

    const handleClearZone = () => {
        setFormData(prev => ({ ...prev, zone: '', zoneName: '', street: '', building: '' }));
        setZoneSearch('');
        setStreets([]);
        setBuildings([]);
        setStreetSearch('');
        setBuildingSearch('');
    };

    const handleClearStreet = () => {
        setFormData(prev => ({ ...prev, street: '', building: '' }));
        setStreetSearch('');
        setBuildings([]);
        setBuildingSearch('');
    };

    const handleClearBuilding = () => {
        setFormData(prev => ({ ...prev, building: '' }));
        setBuildingSearch('');
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name || !formData.mobileNo || !formData.nationality) {
            alert('Please fill all required fields');
            return;
        }

        try {
            setLoading(true);
            if (customer) {
                await axiosInstance.put(`/api/customers/${customer._id}`, formData);
                alert('Customer updated successfully');
            } else {
                await axiosInstance.post('/api/customers', formData);
                alert('Customer created successfully');
            }
            onClose(true);
        } catch (error) {
            console.error('Error saving customer:', error);
            alert(error.response?.data?.message || 'Failed to save customer');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="cf-drawer-overlay" onClick={() => onClose(false)}>
            <div className="cf-drawer" onClick={(e) => e.stopPropagation()}>
                <div className="cf-drawer-header">
                    <h2>{customer ? 'Edit Customer' : 'Add New Customer'}</h2>
                    <button className="cf-close-btn" onClick={() => onClose(false)}>
                        <HiOutlineX size={24} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="cf-form">
                    <div className="cf-form-group">
                        <label>Name <span className="required">*</span></label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Enter customer name"
                            required
                        />
                    </div>

                    <div className="cf-form-group">
                        <label>Mobile Number <span className="required">*</span></label>
                        <input
                            type="tel"
                            name="mobileNo"
                            value={formData.mobileNo}
                            onChange={handleChange}
                            placeholder="Enter mobile number"
                            required
                        />
                    </div>

                    <div className="cf-form-group">
                        <label>Nationality <span className="required">*</span></label>
                        <select
                            name="nationality"
                            value={formData.nationality}
                            onChange={handleChange}
                            required
                        >
                            <option value="">Select nationality</option>
                            {NATIONALITIES.map((nat) => (
                                <option key={nat} value={nat}>{nat}</option>
                            ))}
                        </select>
                    </div>

                    <div className="cf-form-group">
                        <label>Salesman</label>
                        <div className="cf-searchable-select" ref={salesmanDropdownRef}>
                            <div className="cf-search-input-wrapper">
                                <input
                                    type="text"
                                    value={salesmanSearch}
                                    onChange={(e) => {
                                        setSalesmanSearch(e.target.value);
                                        setShowSalesmanDropdown(true);
                                        if (!e.target.value) setFormData(prev => ({ ...prev, salesman: '' }));
                                    }}
                                    onFocus={() => setShowSalesmanDropdown(true)}
                                    placeholder="Search salesman by name or employee ID..."
                                    className="cf-search-input"
                                />
                                {formData.salesman && (
                                    <button
                                        type="button"
                                        className="cf-clear-btn"
                                        onClick={handleClearSalesman}
                                        title="Clear selection"
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                            {showSalesmanDropdown && (
                                <div className="cf-dropdown-list">
                                    {loadingSalesmen ? (
                                        <div className="cf-dropdown-item cf-info">Searching...</div>
                                    ) : salesmen.length > 0 ? (
                                        salesmen.map((sm) => (
                                            <div
                                                key={sm._id}
                                                className={`cf-dropdown-item ${formData.salesman === sm._id ? 'selected' : ''}`}
                                                onClick={() => handleSalesmanSelect(sm)}
                                            >
                                                <strong>{sm.name}</strong>
                                                <span className="cf-zone-name">{sm.employeeId}{sm.branchName ? ` — ${sm.branchName}` : ''}</span>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="cf-dropdown-item cf-no-results">No salesman found</div>
                                    )}
                                </div>
                            )}
                        </div>
                        <small className="cf-field-hint">
                            Optional — leave blank for customers not assigned to a salesman.
                        </small>
                    </div>

                    <div className="cf-section-header">
                        <strong>📍 Address (Optional)</strong>
                    </div>

                    <div className="cf-form-group">
                        <label>Zone Number</label>
                        <div className="cf-searchable-select" ref={zoneDropdownRef}>
                            <div className="cf-search-input-wrapper">
                                <input
                                    type="text"
                                    value={zoneSearch}
                                    onChange={(e) => {
                                        setZoneSearch(e.target.value);
                                        setShowZoneDropdown(true);
                                    }}
                                    onFocus={() => setShowZoneDropdown(true)}
                                    placeholder={loadingZones ? 'Loading zones...' : 'Search zone by number or name...'}
                                    disabled={loadingZones}
                                    className="cf-search-input"
                                />
                                {formData.zone && (
                                    <button
                                        type="button"
                                        className="cf-clear-btn"
                                        onClick={handleClearZone}
                                        title="Clear selection"
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                            {showZoneDropdown && !loadingZones && (
                                <div className="cf-dropdown-list">
                                    {filteredZones.length > 0 ? (
                                        filteredZones.slice(0, 50).map((zone, index) => (
                                            <div
                                                key={`zone-${zone.zone_number}-${index}`}
                                                className={`cf-dropdown-item ${formData.zone === zone.zone_number ? 'selected' : ''}`}
                                                onClick={() => handleZoneSelect(zone)}
                                            >
                                                <strong>Zone {zone.zone_number}</strong>
                                                <span className="cf-zone-name">{zone.zone_name_en}</span>
                                                <span className="cf-zone-name-ar">{zone.zone_name_ar}</span>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="cf-dropdown-item cf-no-results">
                                            No zones found
                                        </div>
                                    )}
                                    {filteredZones.length > 50 && (
                                        <div className="cf-dropdown-item cf-info">
                                            Showing first 50 results. Type to narrow down...
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="cf-form-group">
                        <label>Street Number</label>
                        <div className="cf-searchable-select" ref={streetDropdownRef}>
                            <div className="cf-search-input-wrapper">
                                <input
                                    type="text"
                                    value={streetSearch}
                                    onChange={(e) => {
                                        setStreetSearch(e.target.value);
                                        setShowStreetDropdown(true);
                                    }}
                                    onFocus={() => setShowStreetDropdown(true)}
                                    placeholder={
                                        !formData.zone ? 'Select zone first' :
                                        loadingStreets ? 'Loading streets...' :
                                        'Search street by number or name...'
                                    }
                                    disabled={!formData.zone || loadingStreets}
                                    className="cf-search-input"
                                />
                                {formData.street && (
                                    <button
                                        type="button"
                                        className="cf-clear-btn"
                                        onClick={handleClearStreet}
                                        title="Clear selection"
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                            {showStreetDropdown && !loadingStreets && formData.zone && (
                                <div className="cf-dropdown-list">
                                    {filteredStreets.length > 0 ? (
                                        filteredStreets.slice(0, 50).map((street, index) => (
                                            <div
                                                key={`street-${street.street_number}-${index}`}
                                                className={`cf-dropdown-item ${formData.street === street.street_number ? 'selected' : ''}`}
                                                onClick={() => handleStreetSelect(street)}
                                            >
                                                <strong>Street {street.street_number}</strong>
                                                <span className="cf-zone-name">{street.street_name_en}</span>
                                                <span className="cf-zone-name-ar">{street.street_name_ar}</span>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="cf-dropdown-item cf-no-results">
                                            No streets found
                                        </div>
                                    )}
                                    {filteredStreets.length > 50 && (
                                        <div className="cf-dropdown-item cf-info">
                                            Showing first 50 results. Type to narrow down...
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="cf-form-group">
                        <label>Building Number</label>
                        <div className="cf-searchable-select" ref={buildingDropdownRef}>
                            <div className="cf-search-input-wrapper">
                                <input
                                    type="text"
                                    value={buildingSearch}
                                    onChange={(e) => {
                                        setBuildingSearch(e.target.value);
                                        setShowBuildingDropdown(true);
                                    }}
                                    onFocus={() => setShowBuildingDropdown(true)}
                                    placeholder={
                                        !formData.zone ? 'Select zone first' :
                                        !formData.street ? 'Select street first' :
                                        loadingBuildings ? 'Loading buildings...' :
                                        'Search building number...'
                                    }
                                    disabled={!formData.zone || !formData.street || loadingBuildings}
                                    className="cf-search-input"
                                />
                                {formData.building && (
                                    <button
                                        type="button"
                                        className="cf-clear-btn"
                                        onClick={handleClearBuilding}
                                        title="Clear selection"
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                            {showBuildingDropdown && !loadingBuildings && formData.zone && formData.street && (
                                <div className="cf-dropdown-list">
                                    {filteredBuildings.length > 0 ? (
                                        filteredBuildings.map((building, index) => (
                                            <div
                                                key={`building-${building.building_number}-${index}`}
                                                className={`cf-dropdown-item ${formData.building === building.building_number ? 'selected' : ''}`}
                                                onClick={() => handleBuildingSelect(building)}
                                            >
                                                <strong>Building {building.building_number}</strong>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="cf-dropdown-item cf-no-results">
                                            No buildings found
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Free-text fallback for addresses that do not map onto a
                        valid QNAS zone/street/building. */}
                    <div className="cf-form-group">
                        <label>Other Address</label>
                        <textarea
                            name="otherAddress"
                            value={formData.otherAddress}
                            onChange={handleChange}
                            placeholder="Any address details that don't fit the zone / street / building above"
                            rows={3}
                            className="cf-textarea"
                        />
                        <small className="cf-field-hint">
                            Use this when the customer's zone, street or building isn't available or isn't correct.
                        </small>
                    </div>

                    <div className="cf-form-actions">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => onClose(false)}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                        >
                            {loading ? 'Saving...' : (customer ? 'Update Customer' : 'Create Customer')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CustomerForm;
